import { create } from 'zustand';
import layersTxt from '@/config/layers.txt?raw';
import { parseLayersTxt } from './parse';
import {
  invalidateCachedProbe,
  isProbeStale,
  readCachedProbe,
  writeCachedProbe,
} from './cache';
import { readLayerNotes } from './notes';
import { probeLayer } from './probe';
import { initArcgisRequest } from '../portal';
import type { LayerIndex, LayerRecord } from './types';

const PROBE_CONCURRENCY = 6;

type RegistryState = {
  hydrated: boolean;
  records: LayerRecord[];
  setRecords: (records: LayerRecord[]) => void;
  patch: (id: string, change: Partial<LayerRecord>) => void;
};

export const useLayerRegistry = create<RegistryState>((set) => ({
  hydrated: false,
  records: [],
  setRecords: (records) => set({ hydrated: true, records }),
  patch: (id, change) =>
    set((s) => ({
      records: s.records.map((r) => (r.id === id ? { ...r, ...change } : r)),
    })),
}));

let initPromise: Promise<void> | null = null;

export function initLayerRegistry(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const entries = parseLayersTxt(layersTxt);
    const initial: LayerRecord[] = entries.map((e) => ({ ...e, status: 'stub' }));
    useLayerRegistry.getState().setRecords(initial);

    await initArcgisRequest();

    const queue = [...initial];
    let active = 0;
    await new Promise<void>((resolve) => {
      const next = () => {
        if (queue.length === 0 && active === 0) {
          resolve();
          return;
        }
        while (active < PROBE_CONCURRENCY && queue.length > 0) {
          const rec = queue.shift()!;
          active++;
          void hydrateOne(rec).finally(() => {
            active--;
            next();
          });
        }
      };
      next();
    });
  })();
  return initPromise;
}

async function hydrateOne(rec: LayerRecord): Promise<void> {
  const { patch } = useLayerRegistry.getState();
  try {
    const cached = await readCachedProbe(rec.id);
    const notes = await readLayerNotes(rec.id);

    if (cached && !isProbeStale(cached)) {
      patch(rec.id, { status: 'ready', probe: cached, notes: notes ?? undefined });
      return;
    }

    patch(rec.id, { status: 'probing', notes: notes ?? undefined });
    const probe = await probeLayer(rec.url);
    await writeCachedProbe(rec.id, probe);
    patch(rec.id, { status: 'ready', probe });
  } catch (err) {
    patch(rec.id, {
      status: 'error',
      error: (err as Error).message ?? 'probe failed',
    });
  }
}

export async function refreshLayerMetadata(id: string): Promise<void> {
  const { patch, records } = useLayerRegistry.getState();
  const rec = records.find((r) => r.id === id);
  if (!rec) throw new Error(`Unknown layer id: ${id}`);
  await invalidateCachedProbe(id);
  patch(id, { status: 'probing', error: undefined });
  try {
    const probe = await probeLayer(rec.url);
    await writeCachedProbe(id, probe);
    patch(id, { status: 'ready', probe });
  } catch (err) {
    patch(id, { status: 'error', error: (err as Error).message ?? 'probe failed' });
    throw err;
  }
}

export function selectLayerIndex(): LayerIndex {
  return useLayerRegistry.getState().records;
}

export function findLayer(id: string): LayerRecord | undefined {
  return useLayerRegistry.getState().records.find((r) => r.id === id);
}
